import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";

import DOMPurify from "dompurify";
import marked from "marked";
import { element } from "prop-types";

import { Checkbox, Modal, SimpleLoading } from "~/components";
import { Foot } from "~/components/Modal/styles";
import { useFetch, useApi } from "~/hooks";
import { meContext } from "./context";
import useIdleLogout from "~/hooks/useIdleLogout";
import { useAuth } from "~/services";
import { ChangeLogContainer } from "~/services/store/me/styles";
import { useLocation, useParams } from "react-router-dom";

export { meContext } from './context';
export { BeMeProvider } from './beMeProvider';

export function MeProvider({ children }) {
  const api = useApi();
  const { search } = useLocation();
  const { t } = useTranslation("components");
  const { isAuthenticated, logout } = useAuth();

  let lastLogin = {};

  const storedLastLogin = localStorage.getItem("dd-last-login");

  if (storedLastLogin) {
    try {
      lastLogin = JSON.parse(storedLastLogin);
    } catch (error) {
      console.error("Error parsing last login data:", error);
    }
  }

  const [group, onGroup] = useState(lastLogin);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);
  const [showChangeLogModal, setShowChangeLogModal] = useState(false);

  const query = new URLSearchParams(search).get("q");

  const isLastLoginInHierarchyOrGroup = (user) => {
    const { grupos, hierarquia } = user;

    const idMatch = hierarquia.some(
      ({ id, cliente }) => id === lastLogin.value && cliente === lastLogin.label
    );
    if (idMatch) return true;

    const idGrupoMatch = grupos.some(
      ({  grupo }) => grupo === lastLogin.label
    );
    if (idGrupoMatch) return true;

    return false;
  };

  const meFetcher = async (params) => {
    const response = await api().get(params);

    if (response.status === 200) {
      if (response.data.data) {
        const userAreSameInLastLogin = isLastLoginInHierarchyOrGroup(
          response.data.data
        );

        if (userAreSameInLastLogin) {
          onGroup(lastLogin);
        } else {
          onGroup({});
          localStorage.removeItem("dd-last-login");
        }
      } else onGroup({});
    }

    return response.data;
  };

  const customFetcher = async (params) => {
    const response = await api().get(params);
    return response.data;
  };

  const { data: splashScreen } = useFetch({
    url: isAuthenticated ? "/admin/splashScreen" : null,
    customFetcher,
  });
  const { data: user, isValidating } = useFetch({
    url: isAuthenticated ? "/user/me" : null,
    customFetcher: meFetcher,
  });

  useEffect(() => {
    if (
      query === "diligencia" ||
      query === "cadastral" ||
      query === "batches"
    ) {
      if (splashScreen?.data) {
        setShowChangeLogModal(true);
      }
    }
  }, [splashScreen, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      localStorage.setItem("dd-last-login", JSON.stringify(group));
    }
  }, [group]);

  const closeSplashScreenClick = useCallback(async () => {
    if (doNotShowAgain) {
      await api().delete("/admin/splashScreen");
    }
    setShowChangeLogModal(false);
  }, [doNotShowAgain, setShowChangeLogModal]);

  const newGroup = (val) => {
    onGroup(val);
  };

  const values = {
    isAdmin: user?.data.usuario?.perfil?.id === 5,
    userTypes: { ictsAdmin: 5 },
    user: {
      ...user?.data,
      isVisualizador: () => user?.data.usuario?.perfil?.id === 1,
      isRevisor: () => user?.data.usuario?.perfil?.id === 2,
      isGestor: () => user?.data.usuario?.perfil?.id === 3,
      isMaster: () => user?.data.usuario?.perfil?.id === 4,
      isAdminICTS: () => user?.data.usuario?.perfil?.id === 5,
      isAPI: () => user?.data.usuario?.perfil?.id === 6,
      canOpenBatches: () => !!user?.data?.usuario?.can_open_lots,
      comesFromBecompliance: () => !!user?.data?.usuario?.comesFromBecompliance,
    },
    group,
    newGroup,
  };

  const { showIdleAlert, onShowIdleAlert, formattedTime } = useIdleLogout();

  if (!user) {
    if (isValidating) {
      return <SimpleLoading size={"lg"} />;
    } else {
      logout();
      return null;
    }
  }

  return (
    <meContext.Provider value={values}>
      {children}
      {showIdleAlert && (
        <Modal
          show
          onShow={onShowIdleAlert}
          title={t("modal.idle", { formattedTime })}
          hasActions={false}
          titleOnly
          size="lg"
        />
      )}
      {splashScreen?.data && showChangeLogModal && (
        <Modal
          goBackOnCancel={closeSplashScreenClick}
          show
          title={t("modal.changelog.title")}
          size="lg"
          hasActions={false}
          footer={
            <Foot>
              <span>
                <Checkbox
                  label={t("modal.changelog.do-not-show-again")}
                  onSelected={(evt) => {
                    setDoNotShowAgain(evt.target.checked);
                  }}
                />
              </span>
            </Foot>
          }
        >
          <ChangeLogContainer
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(marked(splashScreen?.data)),
            }}
          />
        </Modal>
      )}
    </meContext.Provider>
  );
}

/**
 * Module hook.
 */
export const useMe = () => useContext(meContext);

MeProvider.propTypes = {
  children: element.isRequired,
};
